import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Farm, Field, Provider } from '../../../api-clients/oem-connection';
import { DialogService } from '../../../services/dialog.service';
import { OemIntegrationService } from '../../../services/oem-integration.service';
import { crossClaimedBy } from '../../../utils/cross-provider-link';
import { PROVIDER_LABELS } from '../integrations.component';

interface RowState {
  /** Selected Harvest Mate farm id (from the farm→farm picker). */
  farmIdInput?: number;
  busy: boolean;
}

/** Minimal shape of a Harvest Mate farm for the link picker. */
interface HmFarm {
  id: number;
  name: string;
  /** Owner (grower) display name — drives the <optgroup> heading (SRA003-502). */
  growerName: string | null;
  /**
   * Annotation when this HM farm is already linked to an OEM farm, or null when
   * free. Set for BOTH the other vendor ("already linked to Case New Holland")
   * and this same vendor ("already linked") — an HM farm maps to exactly one
   * OEM farm, so a taken one is disabled + annotated either way (SRA003-473 +
   * same-provider double-map guard).
   */
  linkedLabel: string | null;
}

/** HM farms grouped by grower for the picker's <optgroup>s (SRA003-502). */
interface HmFarmGroup {
  growerName: string;
  farms: HmFarm[];
}

/**
 * OEM farms link panel (SRA003-440). The farm-level sibling of
 * field-link-panel — kept as a visually distinct section because farms and
 * fields are different entities (Aland's call). Lists the vendor's OEM farms
 * and links each to a Harvest Mate farm; the picker only ever offers HM farms,
 * so a cross-type target (farm→block/harvester) is impossible by construction
 * (the FE half of the -440 type guarantee).
 */
@Component({
  selector: 'app-farm-link-panel',
  templateUrl: './farm-link-panel.component.html',
  styleUrls: ['./farm-link-panel.component.css'],
  standalone: false,
})
export class FarmLinkPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() provider!: Provider;
  @Input() providerLabel = '';

  loading = false;
  errorMessage = '';
  /**
   * Set when the BE returns 503 for the farms list (Deere `deere_unlicensed` —
   * the ag-data license hasn't activated yet; CNH is ungated). Surfaced as a
   * banner, not a toast — mirrors how the fields list handles `cnh_unavailable`.
   */
  farmsUnavailable = false;

  farms: Farm[] = [];
  hmFarms: HmFarm[] = [];
  /** HM farms grouped by grower for the picker's <optgroup>s (SRA003-502). */
  farmGroups: HmFarmGroup[] = [];
  rowState: Record<string, RowState> = {};
  /** Subscription to the field-linkage change signal (SRA003-502 field-first). */
  private fieldLinksSub?: Subscription;

  constructor(
    private oemSvc: OemIntegrationService,
    private dialog: DialogService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    // SRA003-502 field-first: when the sibling field panel links/unlinks a
    // field, it may have auto-linked or auto-unlinked a parent farm — reload
    // the farm list so this panel reflects it without a page refresh.
    this.fieldLinksSub = this.oemSvc.fieldLinksRefresh$.subscribe(() => {
      if (!this.provider) return;
      // SRA003-502: refresh the HM picker too — a sibling field link/unlink can
      // auto-link/unlink a farm, changing which HM farms are "already taken".
      this.loadHmFarms();
      this.loadFarms();
    });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.provider) return;
    this.loadHmFarms();
    this.loadFarms();
  }

  ngOnDestroy(): void {
    this.fieldLinksSub?.unsubscribe();
  }

  /**
   * Harvest Mate farms for the link picker — the farms reachable via the
   * contractor's groups (SRA003-502), grouped by grower for the picker's
   * <optgroup>s. Server returns ACTIVE farms only.
   */
  loadHmFarms(): void {
    this.oemSvc.listLinkableFarms().subscribe({
      next: (res) => {
        this.hmFarms = (res?.farms ?? []).map((f) => ({
          id: f.id,
          name: f.name,
          growerName: f.grower_name,
          linkedLabel: this.hmFarmLinkedLabel(f.linked_provider),
        }));
        this.farmGroups = this.groupByGrower(this.hmFarms);
      },
      error: () => {
        // Surface but don't block the panel — linking just won't work until
        // the HM farm list can be fetched.
        this.hmFarms = [];
        this.farmGroups = [];
      },
    });
  }

  /** Group HM farms by grower name (sorted; owner-less → "Other"). */
  private groupByGrower(farms: HmFarm[]): HmFarmGroup[] {
    const byName = new Map<string, HmFarm[]>();
    for (const f of farms) {
      const key = f.growerName || 'Other';
      const list = byName.get(key);
      if (list) list.push(f);
      else byName.set(key, [f]);
    }
    return [...byName.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([growerName, gFarms]) => ({ growerName, farms: gFarms }));
  }

  loadFarms(): void {
    this.loading = true;
    this.errorMessage = '';
    this.farmsUnavailable = false;
    this.farms = [];
    this.rowState = {};
    this.oemSvc.listFarms(this.provider).subscribe({
      next: (res) => {
        this.farms = res?.farms ?? [];
        this.rowState = {};
        for (const f of this.farms) {
          this.rowState[f.oem_farm_id] = { busy: false };
        }
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.farms = [];
        this.rowState = {};
        // Only the Deere licence gate (503 deere_unlicensed) gets the banner —
        // mirror the field panel's cnh_unavailable check. Any other 503 (or
        // error) is a real failure, surfaced as an error message, not the
        // "licence pending" banner.
        if (this.matchesError(err, 503, 'deere_unlicensed')) {
          this.farmsUnavailable = true;
        } else {
          this.errorMessage = `Could not load ${this.providerLabel} farms`;
        }
      },
    });
  }

  rowFor(farm: Farm): RowState {
    let state = this.rowState[farm.oem_farm_id];
    if (!state) {
      state = { busy: false };
      this.rowState[farm.oem_farm_id] = state;
    }
    return state;
  }

  isLinked(f: Farm): boolean {
    return f.linked_farm_id != null;
  }

  /**
   * SRA003-524: this OEM farm is already linked by ANOTHER contractor in the org
   * (the BE org-wide overlay flag). The picker disables it — linking would 409 —
   * without revealing who holds it (privacy-aware: boolean only).
   */
  isLinkedElsewhere(f: Farm): boolean {
    return !this.isLinked(f) && f.linked_elsewhere === true;
  }

  hmFarmLabel(id: number | null | undefined): string {
    if (id == null) return '';
    const f = this.hmFarms.find((x) => x.id === id);
    return f ? f.name || `Farm ${id}` : `Farm ${id}`;
  }

  /**
   * Annotation for an HM farm already linked to an OEM farm — non-null also
   * disables the picker option so an HM farm can't be mapped to two OEM farms.
   * "already linked to <vendor>" when the OTHER vendor claims it (SRA003-473),
   * or just "already linked" when this same provider already linked it to a
   * different OEM farm. Null = free.
   */
  private hmFarmLinkedLabel(linkedProvider: unknown): string | null {
    const other = crossClaimedBy(linkedProvider, this.provider);
    if (other) return `already linked to ${PROVIDER_LABELS[other]}`;
    if (linkedProvider === this.provider) return 'already linked';
    return null;
  }

  link(farm: Farm): void {
    const state = this.rowFor(farm);
    const farmId = state.farmIdInput;
    if (farmId == null) {
      this.toastr.warning('Select a Harvest Mate farm to link to first');
      return;
    }
    state.busy = true;
    this.oemSvc.linkFarm(this.provider, farm.oem_farm_id, farmId).subscribe({
      next: () => {
        state.busy = false;
        const farmName = farm.name || farm.oem_farm_id;
        this.toastr.success(`Linked ${farmName} to ${this.hmFarmLabel(farmId)}`);
        // SRA003-502: refresh the HM picker so the just-linked farm flips to
        // "already linked" (disabled) without a page reload.
        this.loadHmFarms();
        this.loadFarms();
        this.oemSvc.requestRefresh();
        // SRA003-502: let the sibling field panel re-run its farm pre-fill.
        this.oemSvc.notifyFarmLinksChanged();
      },
      error: (err: HttpErrorResponse) => {
        state.busy = false;
        if (this.matchesError(err, 400, 'invalid_link_target')) {
          this.toastr.error('That target is not a Harvest Mate farm.');
        } else if (err?.status === 403) {
          // SRA003-517: the harvester_crop_assignments prereq surfaces as a
          // 403 (typed `farm_not_assigned`). Scope the friendly copy to any
          // 403 on the link endpoint so a drift in the typed code still yields
          // the prereq guidance rather than the raw generic "Could not link".
          // The only other 403 here is the contractor role gate, unreachable
          // from this contractor-only panel.
          this.toastr.error(
            'You can link this farm once a block in it has a crop assignment.',
          );
        } else if (
          err?.status === 409 &&
          err?.error?.errorType === 'already_linked' &&
          err?.error?.errorMessage
        ) {
          // SRA003-516: reject-don't-replace. The BE returns 409
          // errorType="already_linked" with a fully-composed friendly
          // errorMessage naming the incumbent ("This farm is already linked to
          // CaseIH (xxx). Unlink it first." — CNH renders as "CaseIH"). This
          // now covers BOTH same- and cross-vendor conflicts, superseding the
          // old cross_provider_link_conflict branch. Show it verbatim — the
          // incumbent is baked into the string. Note the envelope key is
          // errorType (camelCase), which matchesError's snake_case
          // code/reason/error_type won't catch. The generic else below is the
          // fallback when errorMessage is absent.
          this.toastr.error(err.error.errorMessage);
        } else {
          this.toastr.error(`Could not link ${farm.name || farm.oem_farm_id}`);
        }
      },
    });
  }

  unlink(farm: Farm): void {
    // SRA003-502: a farm's fields are linked to blocks in the farm's Harvest
    // Mate farm, so unlinking the farm must cascade to those field links.
    // Fetch the current fields to find which ones belong to this OEM farm and
    // are still linked, so the confirm dialog can warn about them.
    this.oemSvc.listFields(this.provider).subscribe({
      next: (res) =>
        this.confirmAndUnlink(
          farm,
          this.linkedFieldsForFarm(res?.fields ?? [], farm),
        ),
      // If the field list can't be loaded, fall back to a farm-only unlink
      // rather than blocking the action.
      error: () => this.confirmAndUnlink(farm, []),
    });
  }

  /** Active field links under an OEM farm (drives the cascade-unlink warning). */
  private linkedFieldsForFarm(fields: Field[], farm: Farm): Field[] {
    return fields.filter(
      (f) => f.farm_id === farm.oem_farm_id && f.linked_block_id != null,
    );
  }

  private confirmAndUnlink(farm: Farm, linkedFields: Field[]): void {
    const farmName = farm.name || farm.oem_farm_id;
    const n = linkedFields.length;
    const message =
      n > 0
        ? `Unlink ${farmName} from its Harvest Mate farm? This will also unlink ${n} field${n === 1 ? '' : 's'} currently mapped to its blocks. Telemetry will stop being attributed until you re-link.`
        : `Unlink ${farmName} from its Harvest Mate farm? Telemetry will stop being attributed to that farm until you re-link.`;
    this.dialog
      .confirmDialog({
        title: 'Unlink farm',
        message,
        confirmCaption: 'Unlink',
        cancelCaption: 'Cancel',
      })
      .subscribe((yes) => {
        if (!yes) return;
        this.performUnlink(farm, linkedFields);
      });
  }

  /**
   * Unlink the farm's linked fields (best-effort) then the farm itself, and
   * refresh both panels. Field unlinks are wrapped so one failure doesn't abort
   * the farm unlink the contractor confirmed; the field panel reloads via
   * notifyFarmLinksChanged so the now-unlinked fields clear.
   */
  private performUnlink(farm: Farm, linkedFields: Field[]): void {
    const state = this.rowFor(farm);
    state.busy = true;
    const fieldUnlinks = linkedFields.map((f) =>
      this.oemSvc
        .unlinkField(this.provider, f.oem_field_id)
        .pipe(catchError(() => of(null))),
    );
    const fields$ = fieldUnlinks.length ? forkJoin(fieldUnlinks) : of([]);
    fields$.subscribe(() => {
      this.oemSvc.unlinkFarm(this.provider, farm.oem_farm_id).subscribe({
        next: () => {
          state.busy = false;
          this.toastr.success(`Unlinked ${farm.name || farm.oem_farm_id}`);
          // SRA003-502: refresh the HM picker so the freed farm becomes
          // selectable again immediately.
          this.loadHmFarms();
          this.loadFarms();
          this.oemSvc.requestRefresh();
          this.oemSvc.notifyFarmLinksChanged();
        },
        error: () => {
          state.busy = false;
          this.toastr.error(`Could not unlink ${farm.name || farm.oem_farm_id}`);
        },
      });
    });
  }

  /**
   * Match a BE ModelError envelope across `code` / `reason` / `error_type`
   * (mirrors field-link-panel). Returns false for non-JSON bodies so the
   * caller falls back to the generic toast.
   */
  private matchesError(
    err: HttpErrorResponse,
    status: number,
    expected: string,
  ): boolean {
    if (err?.status !== status) return false;
    const body = err?.error;
    if (!body || typeof body !== 'object') return false;
    return (
      body.code === expected ||
      body.reason === expected ||
      body.error_type === expected
    );
  }
}
